import React from 'react';
import {
  Box, Button, Chip, IconButton, MenuItem, Stack, Select, TextField, Typography, ToggleButton,
  ToggleButtonGroup, Paper,
} from '@mui/material';
import { Icon } from '@iconify/react';
import plusFill from '@iconify/icons-eva/plus-fill';
import closeFill from '@iconify/icons-eva/close-fill';

const FIELDS = [
  { key: 'industry',     label: 'Industry',         type: 'enum',  options: ['SaaS', 'Fintech', 'Healthcare', 'Ecommerce', 'D2C'] },
  { key: 'country',      label: 'Country',          type: 'multi', options: ['US', 'UK', 'IN', 'SG', 'JP', 'DE', 'FR'] },
  { key: 'fte',          label: 'Employee count',   type: 'number' },
  { key: 'revenue',      label: 'Revenue (USD)',    type: 'number' },
  { key: 'foundation_year', label: 'Founded year',  type: 'number' },
  { key: 'has_email',    label: 'Has email',        type: 'bool' },
  { key: 'has_linkedin', label: 'Has LinkedIn',     type: 'bool' },
];

const OPERATORS_BY_TYPE = {
  enum:   [{ k: 'eq', l: 'equals' }, { k: 'in', l: 'in' }],
  multi:  [{ k: 'in', l: 'in' }],
  number: [{ k: 'eq', l: 'equals' }, { k: 'gte', l: '≥' }, { k: 'lte', l: '≤' }, { k: 'between', l: 'between' }],
  bool:   [{ k: 'isnotnull', l: 'is set' }, { k: 'eq', l: 'equals' }],
};

const fieldByKey = (key) => FIELDS.find((f) => f.key === key) || FIELDS[0];

const ValueEditor = ({ rule, onChange }) => {
  const f = fieldByKey(rule.field);
  if (f.type === 'enum') {
    if (rule.operator === 'in') {
      const arr = Array.isArray(rule.value) ? rule.value : [];
      return (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" alignItems="center">
          {arr.map((v) => (
            <Chip key={v} label={v} size="small"
                  onDelete={() => onChange({ value: arr.filter((x) => x !== v) })} />
          ))}
          <Select size="small" value="" displayEmpty
                  onChange={(e) => onChange({ value: [...arr, e.target.value] })}
                  sx={{ minWidth: 120 }}>
            <MenuItem value="" disabled>+ add</MenuItem>
            {f.options.filter((o) => !arr.includes(o)).map((o) =>
              <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
        </Stack>
      );
    }
    return (
      <Select size="small" value={rule.value || ''} onChange={(e) => onChange({ value: e.target.value })}
              sx={{ minWidth: 160 }}>
        {f.options.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
      </Select>
    );
  }
  if (f.type === 'multi') {
    const arr = Array.isArray(rule.value) ? rule.value : [];
    return (
      <Stack direction="row" spacing={0.75} flexWrap="wrap" alignItems="center">
        {arr.map((v) => (
          <Chip key={v} label={v} size="small"
                onDelete={() => onChange({ value: arr.filter((x) => x !== v) })} />
        ))}
        <Select size="small" value="" displayEmpty
                onChange={(e) => onChange({ value: [...arr, e.target.value] })}
                sx={{ minWidth: 120 }}>
          <MenuItem value="" disabled>+ add</MenuItem>
          {f.options.filter((o) => !arr.includes(o)).map((o) =>
            <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </Select>
      </Stack>
    );
  }
  if (f.type === 'number') {
    if (rule.operator === 'between') {
      const [min, max] = Array.isArray(rule.value) ? rule.value : ['', ''];
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField size="small" type="number" value={min}
                     onChange={(e) => onChange({ value: [Number(e.target.value), max] })}
                     sx={{ width: 100 }} />
          <Typography variant="body2">–</Typography>
          <TextField size="small" type="number" value={max}
                     onChange={(e) => onChange({ value: [min, Number(e.target.value)] })}
                     sx={{ width: 100 }} />
        </Stack>
      );
    }
    return (
      <TextField size="small" type="number"
                 value={typeof rule.value === 'number' ? rule.value : ''}
                 onChange={(e) => onChange({ value: Number(e.target.value) })} sx={{ width: 140 }} />
    );
  }
  if (f.type === 'bool') {
    if (rule.operator === 'isnotnull') return null;
    return (
      <Select size="small" value={rule.value ?? ''} onChange={(e) => onChange({ value: e.target.value })}
              sx={{ minWidth: 120 }}>
        <MenuItem value={true}>true</MenuItem>
        <MenuItem value={false}>false</MenuItem>
      </Select>
    );
  }
  return null;
};

const Rule = ({ rule, onChange, onRemove }) => {
  const f = fieldByKey(rule.field);
  const ops = OPERATORS_BY_TYPE[f.type] || [];
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.75 }}>
      <Select size="small" value={rule.field}
              onChange={(e) => {
                const newF = fieldByKey(e.target.value);
                onChange({ field: e.target.value, operator: OPERATORS_BY_TYPE[newF.type][0].k, value: null });
              }}
              sx={{ minWidth: 180 }}>
        {FIELDS.map((opt) => <MenuItem key={opt.key} value={opt.key}>{opt.label}</MenuItem>)}
      </Select>
      <Select size="small" value={rule.operator} onChange={(e) => onChange({ operator: e.target.value })}
              sx={{ minWidth: 120 }}>
        {ops.map((op) => <MenuItem key={op.k} value={op.k}>{op.l}</MenuItem>)}
      </Select>
      <Box sx={{ flex: 1 }}>
        <ValueEditor rule={rule} onChange={onChange} />
      </Box>
      <IconButton size="small" onClick={onRemove}><Icon icon={closeFill} /></IconButton>
    </Stack>
  );
};

const RuleGroupBuilder = ({ rules = [], onChange }) => {
  const [combinator, setCombinator] = React.useState('ALL');

  const addRule = () => onChange([
    ...rules,
    { id: Date.now(), group: 1, field: 'industry', operator: 'eq', value: 'SaaS' },
  ]);

  const updateRule = (idx, patch) => {
    const next = [...rules];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const removeRule = (idx) => onChange(rules.filter((_, i) => i !== idx));

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <ToggleButtonGroup
          size="small" exclusive value={combinator}
          onChange={(_, v) => v && setCombinator(v)}
        >
          <ToggleButton value="ALL">ALL of</ToggleButton>
          <ToggleButton value="ANY">ANY of</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">
          {rules.length} rule{rules.length !== 1 && 's'}
        </Typography>
      </Stack>

      {rules.map((r, i) => (
        <Rule key={r.id || i} rule={r} onChange={(patch) => updateRule(i, patch)}
              onRemove={() => removeRule(i)} />
      ))}

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <Button size="small" startIcon={<Icon icon={plusFill} />} onClick={addRule}>
          Add rule
        </Button>
      </Stack>
    </Paper>
  );
};

export default RuleGroupBuilder;
